export const queryText = (query, key) => (typeof query[key] === 'string' ? query[key] : '')

const DAY_MS = 86400000

export const localDate = date => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date).map(part => [part.type, part.value]))
  return `${parts.year}-${parts.month}-${parts.day}`
}

// Parse wall-clock values without the browser timezone. Date-only links retain
// their inclusive whole-day meaning; explicit times use an exclusive endpoint.
const dateBoundary = (value, end = false) => {
  const match = /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2}))?$/.exec(value)
  if (!match || match[1].startsWith('0000')) return NaN
  const iso = `${match[1]}T${match[2] || '00:00'}:00.000Z`
  const date = new Date(iso)
  if (!Number.isFinite(date.getTime()) || date.toISOString() !== iso) return NaN
  const time = date.getTime() + (end && !match[2] ? DAY_MS : 0)
  return new Date(time).getUTCFullYear() > 9999 ? NaN : time
}

export const normalizePoyDateTime = (value, end = false) => {
  const time = dateBoundary(value, end)
  return Number.isFinite(time) ? new Date(time).toISOString().slice(0, 16).replace('T', ' ') : value
}

export const poyDatePreset = (days, now = new Date()) => {
  const today = dateBoundary(localDate(now))
  const format = time => new Date(time).toISOString().slice(0, 16).replace('T', ' ')
  return { startDate: format(today - (days - 1) * DAY_MS), endDate: format(today + DAY_MS) }
}

export const poyDateLabel = value => {
  const [date, time] = value.replace('T', ' ').split(' ')
  return `${date.split('-').reverse().join('/')}${time ? ` ${time}` : ''}`
}

export const validRange = (start, end) => {
  const duration = dateBoundary(end, true) - dateBoundary(start)
  return Number.isFinite(duration) && duration > 0 && duration <= 31 * DAY_MS
}

export const readPoyQuery = query => {
  const defaults = poyDatePreset(1)
  return {
    agent: queryText(query, 'agent'),
    startDate: normalizePoyDateTime(queryText(query, 'startDate') || defaults.startDate),
    endDate: normalizePoyDateTime(queryText(query, 'endDate') || defaults.endDate, true),
    poyId: queryText(query, 'poyId'),
    rowId: queryText(query, 'rowId'),
    username: queryText(query, 'username'),
    type: queryText(query, 'type'),
    status: queryText(query, 'status'),
    number: queryText(query, 'number'),
    betType: queryText(query, 'betType'),
    page: Math.max(1, Number.parseInt(queryText(query, 'page'), 10) || 1),
    limit: [10, 25, 50, 100].includes(Number(query.limit)) ? Number(query.limit) : 25,
  }
}

export const reportQueryString = params => new URLSearchParams(Object.fromEntries(
  Object.entries(params).filter(([, value]) => value !== '' && value !== null && value !== undefined),
)).toString()

export const betTypeLabels = (huayId, type) => {
  if (String(huayId).length === 24) return ['สามตัวบน', 'สามตัวโต้ด', 'สองตัวบน', 'สองตัวล่าง', 'วิ่งบน', 'วิ่งล่าง']
  const labels = [
    'สามตัวบน', 'สามตัวโต้ด', 'สามตัวหน้า', 'สามตัวท้าย', 'สองตัวบน', 'สองตัวล่าง',
    'วิ่งบน', 'วิ่งล่าง',
  ]
  // 9 = เลขชุดของหวยชุด · 4/5/6 ตัวหลังเดิม (9–11 ของหวยอื่น) ตัดทิ้งแล้ว — แทนด้วย 4/5 ตัว (FOUR_FIVE_DIGIT_BETS ข้อ 11)
  return [4, 5].includes(Number(type)) ? [...labels, 'เลขชุด'] : labels
}

// ป้ายประเภท 4/5 ตัว (ตรงกับ src/utils/betTypes.js) — ประกาศในไฟล์เพราะ tests/poy-date-range.cjs โหลดไฟล์นี้ตรง ๆ แบบไม่มี import
const FOUR_FIVE_DIGIT_LABELS = {
  41: 'สี่ตัวบน', 42: 'สี่ตัวโต๊ด', 51: 'ห้าตัวบน', 52: 'ห้าตัวโต๊ด',
}

// ป้ายของประเภทแทงหนึ่งรายการ — ประเภท 4/5 ตัว (หวยและยี่กีใช้รหัสเดียวกัน) อ่านตามรหัส
export const betTypeLabel = (huayId, betType, type) => FOUR_FIVE_DIGIT_LABELS[betType]
  || betTypeLabels(huayId, type)[betType - 1] || String(betType)

export const statusKey = status => ({
  '-1': 'Refund', 0: 'Waiting', 1: 'Win', 2: 'Lose',
}[status] || 'Status')
export const statusVariant = status => ({
  '-1': 'light-warning', 0: 'light-secondary', 1: 'light-success', 2: 'light-danger',
}[status] || 'light-secondary')
export const money = value => Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
export const reportDateTime = value => (value ? new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Bangkok', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
}).format(new Date(value)) : '—')
